import React from 'react';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  createRoundingConditionAssortmentSchema,
  createRoundingConditionSchema,
  CreateRoundingConditionSchema,
  getConditionBindingValuesFromLevelSchema,
} from '@retail/calculus/components';
import { CreatePriceRoundingConditionContent } from './CreatePriceRoundingConditionContent';
import { useConditionActions } from '@retail/calculus/context';
import { SupplierBase } from '@retail/suppliers/types';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { CreateConditionDialog } from '../ConditionActionsBase';

interface Props {
  open: boolean;
  onClose: () => void;
  supplier?: SupplierBase;
}

const CreatePriceRoundingConditionDialog: React.FC<Props> = ({ open, onClose, supplier }) => {
  const t = useCalculusTFunction();
  const { selectedOrgUnitAssortment } = useSelectedOrgUnit();

  const formMethods = useForm<CreateRoundingConditionSchema>({
    resolver: supplier
      ? yupResolver(createRoundingConditionSchema(t))
      : yupResolver(createRoundingConditionAssortmentSchema(t)),
    defaultValues: {
      ...createRoundingConditionSchema(t).getDefault(),
      level: supplier ? '' : 'assortment',
    },
  });
  const { handleSubmit } = formMethods;

  const { createOrUpdateCondition, isLoadingPostChanges } = useConditionActions();

  const onSubmit = handleSubmit((levelSchema) => {
    createOrUpdateCondition({
      content: {
        rounding: { roundingRuleSetId: levelSchema.priceRoundingId },
      },
      bindingValue: getConditionBindingValuesFromLevelSchema({
        levelSchema,
        supplierNumber: supplier?.supplierNumber,
      }),
      validFrom: levelSchema.fromDate,
    }).then(onClose);
  });

  return (
    <FormProvider {...formMethods}>
      <CreateConditionDialog
        open={open}
        title={t('calculus.rounding.createConditionTitle', {
          name: supplier ? supplier.name : selectedOrgUnitAssortment.name,
        })}
        supplierNumber={supplier?.supplierNumber}
        dialogContent={<CreatePriceRoundingConditionContent />}
        isLoading={isLoadingPostChanges}
        onSubmit={onSubmit}
        onClose={onClose}
      />{' '}
    </FormProvider>
  );
};

export default withDialogSuspenseErrorBoundary(CreatePriceRoundingConditionDialog);
